// Why files failed, counted per batch, so the next failure explains itself — on the failed rows, in one summary
// notice ("8,925 failed: Safari couldn't read the files (NotReadableError)"), in the upload-session report the
// server keeps (support reads it with diag-upload-attempts), and in the "Report a problem" text a person can copy.
// Counts and error NAMES only: never file names or paths.

/** Where in the pipeline a file failed. */
export type FailKind =
  | "read" // the browser couldn't read the local file (NotReadableError, NotFoundError, SecurityError …)
  | "copy" // the preview copy couldn't be made (decode / canvas / encoder)
  | "mint" // the server gave no upload slot
  | "upload" // the upload itself failed (network, storage refused it)
  | "plan" // the workspace's plan refused it
  | "not-tried" // the batch stopped before it (nothing before it could be read)
  | "internal"; // the uploader hit an unexpected error

/** A count key: `${kind}:${name}`, e.g. "read:NotReadableError", "mint:403", "upload:network". */
export type FailKey = `${FailKind}:${string}`;

const NAME_RE = /[^A-Za-z0-9 _.-]/g;

/** A short, safe error name: the DOMException / Error name ("NotReadableError"), else its message's first words. */
export function errorName(e: unknown): string {
  const err = e as { name?: unknown; message?: unknown } | null;
  const raw =
    (err && typeof err.name === "string" && err.name !== "Error" && err.name) ||
    (err && typeof err.message === "string" && err.message) ||
    (typeof e === "string" ? e : "") ||
    "unknown";
  return String(raw).replace(NAME_RE, "").trim().slice(0, 40) || "unknown";
}

/** The class of an upload error from its message (content.ts: "Upload failed (403)", "Network error during upload"). */
export function uploadErrorName(message: string): string {
  const m = /\((\d{3})\)/.exec(message);
  if (m) return m[1];
  if (/network/i.test(message)) return "network";
  if (/stalled/i.test(message)) return "stalled";
  if (/timed? ?out/i.test(message)) return "timeout";
  if (/abort/i.test(message)) return "aborted";
  return errorName(message);
}

/** A mint error's class: the HTTP status, "network", or the error's name. */
export function mintErrorName(status: number | undefined, e: unknown): string {
  if (status) return String(status);
  const msg = String((e as { message?: unknown } | null)?.message ?? "");
  const n = errorName(e);
  // A failed fetch: Safari "Load failed", Chrome "Failed to fetch", Firefox "NetworkError when attempting to fetch".
  return /network|fetch|load failed/i.test(`${n} ${msg}`) ? "network" : n;
}

// Tolerates a missing name: counting a failure must never throw (a throw here once left a file uncounted).
export const failKey = (kind: FailKind, name: string | undefined | null): FailKey =>
  `${kind}:${String(name ?? "").replace(NAME_RE, "").slice(0, 40) || "unknown"}`;

/** Failures the uploader retries by itself before reporting them: the upload itself (a dropped or stalled
 *  connection, a storage hiccup) or no upload slot for a reason that can pass (network, timeout, 5xx, 429).
 *  Not: unreadable files, previews this browser can't make, plan limits, or a mint the server refused outright. */
export const autoRetryable = (key?: string): boolean =>
  !!key && (key.startsWith("upload:") || (key.startsWith("mint:") && !/^mint:(400|401|403|404|409|413|422)$/.test(key)));

/** Add `n` to a batch's reason counts (mutates and returns them). */
export function countFail(counts: Record<string, number> | undefined, key: FailKey, n = 1): Record<string, number> {
  const c = counts ?? {};
  c[key] = (c[key] ?? 0) + n;
  return c;
}

/** Merge several batches' counts. */
export function mergeCounts(all: Array<Record<string, number> | undefined>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const c of all) if (c) for (const [k, v] of Object.entries(c)) out[k] = (out[k] ?? 0) + v;
  return out;
}

// ---- the browser / OS, from the user agent (Safari freezes its macOS version, so only the OS is named) ----
export interface ClientInfo {
  browser: string;
  os: string;
  ua: string;
  cores?: number;
  memoryGB?: number;
}

export function parseClient(ua: string): { browser: string; os: string } {
  const v = (re: RegExp) => re.exec(ua)?.[1];
  let browser = "a browser";
  if (/Edg\//.test(ua)) browser = `Edge ${v(/Edg\/(\d+)/)}`;
  else if (/(?:Chrome|CriOS)\//.test(ua) && !/Chromium\/|OPR\//.test(ua)) browser = `Chrome ${v(/(?:Chrome|CriOS)\/(\d+)/)}`;
  else if (/(?:Firefox|FxiOS)\//.test(ua)) browser = `Firefox ${v(/(?:Firefox|FxiOS)\/(\d+)/)}`;
  else if (/Safari\//.test(ua) && /Version\//.test(ua)) browser = `Safari ${v(/Version\/(\d+(?:\.\d+)?)/)}`;
  const os = /iPhone|iPad|iPod/.test(ua) ? "iOS" : /Android/.test(ua) ? "Android" : /Mac OS X|Macintosh/.test(ua) ? "macOS"
    : /Windows/.test(ua) ? "Windows" : /CrOS/.test(ua) ? "ChromeOS" : /Linux/.test(ua) ? "Linux" : "unknown OS";
  return { browser, os };
}

export function clientInfo(): ClientInfo {
  const nav = typeof navigator !== "undefined" ? navigator : undefined;
  const ua = nav?.userAgent ?? "";
  const mem = (nav as Navigator & { deviceMemory?: number } | undefined)?.deviceMemory;
  return { ...parseClient(ua), ua: ua.slice(0, 300), cores: nav?.hardwareConcurrency || undefined, memoryGB: mem || undefined };
}

/** "Safari" from "Safari 26.6" — the name a person knows. */
const browserName = (browser: string) => browser.replace(/\s+[\d.]+$/, "") || "Your browser";

/** What a reason means, in a few words (the summary notice and the failed rows). */
export function failLabel(key: string, browser = clientInfo().browser): string {
  const [kind, name = "unknown"] = key.split(":");
  const b = browserName(browser);
  switch (kind) {
    case "read":
      // Safari also answers NotFoundError for a file that was only CHANGED (its modification time) after it was added.
      if (name === "NotFoundError") return `${b} couldn't read the files: changed, moved or deleted after they were added (NotFoundError)`;
      if (name === "SecurityError") return `${b} wasn't allowed to read the files (SecurityError)`;
      if (name === "QuotaExceededError") return `${b} ran out of room while reading the files (QuotaExceededError)`;
      return `${b} couldn't read the files (${name})`;
    case "copy":
      return `${b} couldn't make preview copies (${name})`;
    case "mint":
      return name === "network" ? "the server couldn't be reached for upload slots (network)" : `the server gave no upload slots (${name})`;
    case "upload":
      if (name === "network") return "the uploads lost their connection (network)";
      if (name === "stalled") return "the uploads stopped getting through: the connection went quiet (stalled)";
      if (/^\d{3}$/.test(name)) return `storage refused the uploads (HTTP ${name})`;
      return `the uploads failed (${name})`;
    case "plan":
      return "not included in the workspace's plan";
    case "not-tried":
      return "not tried: the files before them couldn't be read";
    default:
      return `the uploader hit an error (${name})`;
  }
}

/** The short reason shown on one failed row. */
export function failRowLabel(key: string | undefined): string | undefined {
  if (!key) return undefined;
  const [kind, name] = key.split(":");
  switch (kind) {
    case "read": return name === "NotFoundError" ? "File changed or moved" : "Can't read file";
    case "copy": return "Preview not made";
    case "mint": return "No upload slot";
    case "upload": return name === "network" ? "Connection lost" : name === "stalled" ? "Connection stalled" : /^\d{3}$/.test(name ?? "") ? "Upload refused" : "Upload failed";
    case "plan": return "Not in plan";
    case "not-tried": return "Not tried";
    default: return "Error";
  }
}

/** Reasons, most files first. */
export const topReasons = (counts: Record<string, number> | undefined): Array<[string, number]> =>
  Object.entries(counts ?? {}).filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1]);

/** "8,925 failed: Safari couldn't read the files (NotReadableError)" — and the other reasons, if any. */
export function failureSummary(failed: number, counts: Record<string, number> | undefined, browser?: string): { title: string; more: string[] } {
  const top = topReasons(counts);
  const n = failed.toLocaleString("en-GB");
  if (!top.length) return { title: `${n} failed`, more: [] };
  const [k0] = top[0];
  const title = `${n} failed: ${failLabel(k0, browser)}`;
  const more = top.slice(1, 4).map(([k, c]) => `${c.toLocaleString("en-GB")}: ${failLabel(k, browser)}`);
  return { title, more };
}
